import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Activity, Calculator, Utensils, ChefHat, BadgeCheck, Check, Clock } from 'lucide-react';

const STEPS = {
  fr: [
    { icon: Activity, label: 'Analyse de votre profil métabolique' },
    { icon: Calculator, label: 'Optimisation de vos macronutriments' },
    { icon: Utensils, label: 'Construction de vos menus personnalisés' },
    { icon: ChefHat, label: 'Sélection de vos recettes' },
    { icon: BadgeCheck, label: 'Finalisation de votre programme' },
  ],
  en: [
    { icon: Activity, label: 'Analyzing your metabolic profile' },
    { icon: Calculator, label: 'Optimizing your macronutrients' },
    { icon: Utensils, label: 'Building your personalized menus' },
    { icon: ChefHat, label: 'Selecting your recipes' },
    { icon: BadgeCheck, label: 'Finalizing your program' },
  ],
};

const stepForProgress = (p) => {
  if (p < 20) return 0;
  if (p < 42) return 1;
  if (p < 66) return 2;
  if (p < 86) return 3;
  return 4;
};

const ProgramBuildingLoader = ({ language = 'fr', progressStep = null }) => {
  const steps = STEPS[language === 'en' ? 'en' : 'fr'];
  const [progress, setProgress] = useState(4);

  // Plafond piloté par l'étape RÉELLE renvoyée par le backend (progression honnête)
  const STEP_CEIL = { plan: 58, images: 90, done: 100 };
  const ceiling = progressStep ? (STEP_CEIL[progressStep] ?? 92) : 92;

  useEffect(() => {
    const id = setInterval(() => {
      setProgress((p) => {
        if (p >= ceiling) return ceiling;
        // Sprint final plus rapide dès que le backend confirme que tout est prêt (évite
        // une longue approche asymptotique qui laisserait la jauge "collée" avant 100%).
        const factor = ceiling >= 100 ? 0.25 : 0.05;
        const floor = ceiling >= 100 ? 3 : 0.4;
        const inc = Math.max(floor, (ceiling - p) * factor);
        return Math.min(ceiling, p + inc);
      });
    }, 480);
    return () => clearInterval(id);
  }, [ceiling]);

  const current = stepForProgress(progress);
  const R = 78;
  const C = 2 * Math.PI * R;
  const dash = C * (1 - progress / 100);

  return (
    <div className="relative flex flex-col items-center justify-center py-16 overflow-hidden" data-testid="pro-loading">
      {/* Ambient glow */}
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
        <motion.div
          className="w-80 h-80 rounded-full"
          style={{ background: 'radial-gradient(circle, rgba(233,30,99,0.25), transparent 70%)' }}
          animate={{ scale: [1, 1.25, 1], opacity: [0.5, 0.85, 0.5] }}
          transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
        />
      </div>

      {/* Floating particles */}
      {[...Array(8)].map((_, i) => (
        <motion.span
          key={i}
          className="absolute w-1.5 h-1.5 rounded-full"
          style={{ background: i % 2 ? '#FBBF24' : '#E91E63', left: `${12 + i * 10}%`, top: '50%' }}
          animate={{ y: [0, -60 - i * 8, 0], opacity: [0, 1, 0] }}
          transition={{ duration: 2.4 + (i % 3) * 0.6, repeat: Infinity, delay: i * 0.25, ease: 'easeInOut' }}
        />
      ))}

      {/* Core ring */}
      <div className="relative" style={{ width: 200, height: 200 }}>
        {/* rotating outer rings */}
        <motion.div
          className="absolute inset-0 rounded-full border-2 border-dashed border-primary/30"
          animate={{ rotate: 360 }}
          transition={{ duration: 12, repeat: Infinity, ease: 'linear' }}
        />
        <motion.div
          className="absolute inset-4 rounded-full border border-yellow-500/20"
          animate={{ rotate: -360 }}
          transition={{ duration: 9, repeat: Infinity, ease: 'linear' }}
        />
        {/* progress SVG */}
        <svg width="200" height="200" className="absolute inset-0 -rotate-90">
          <defs>
            <linearGradient id="loaderGrad" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#E91E63" />
              <stop offset="100%" stopColor="#FBBF24" />
            </linearGradient>
          </defs>
          <circle cx="100" cy="100" r={R} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="6" />
          <circle
            cx="100" cy="100" r={R} fill="none" stroke="url(#loaderGrad)" strokeWidth="6"
            strokeLinecap="round" strokeDasharray={C} strokeDashoffset={dash}
            style={{ transition: 'stroke-dashoffset 0.5s ease' }}
          />
        </svg>
        {/* center % */}
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="calc-display text-5xl font-black text-white tabular-nums">{Math.round(progress)}%</span>
          <span className="text-[11px] tracking-[0.2em] text-zinc-500 uppercase mt-1">{language === 'en' ? 'Building' : 'Création'}</span>
        </div>
      </div>

      {/* Title + current step */}
      <h3 className="calc-display text-xl sm:text-2xl font-extrabold text-white mt-8 text-center px-4">
        {language === 'en' ? 'Crafting your tailor-made program' : 'Élaboration de votre programme sur-mesure'}
      </h3>
      <p className="flex items-center gap-1.5 text-xs text-zinc-500 mt-2" data-testid="loader-reassurance-message">
        <Clock className="w-3.5 h-3.5 text-yellow-400/80" />
        {language === 'en' ? "Can take a few minutes for a fully tailored result — no need to reload the page" : 'Peut prendre quelques minutes pour un résultat 100% sur-mesure — inutile de recharger la page'}
      </p>
      <div className="h-6 mt-2">
        <AnimatePresence mode="wait">
          <motion.p
            key={current}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.35 }}
            className="text-sm text-primary font-semibold"
          >
            {steps[current].label}…
          </motion.p>
        </AnimatePresence>
      </div>

      {/* Step checklist */}
      <div className="mt-7 w-full max-w-sm space-y-2.5 px-4">
        {steps.map((s, i) => {
          const done = i < current;
          const active = i === current;
          const Icon = s.icon;
          return (
            <motion.div
              key={i}
              initial={{ opacity: 0, x: -12 }}
              animate={{ opacity: done || active ? 1 : 0.4, x: 0 }}
              transition={{ duration: 0.4, delay: i * 0.05 }}
              className={`flex items-center gap-3 rounded-xl px-3.5 py-2.5 border transition-colors ${
                active ? 'bg-primary/10 border-primary/40' : done ? 'bg-white/5 border-white/10' : 'border-transparent'
              }`}
            >
              <span className={`flex items-center justify-center w-7 h-7 rounded-lg shrink-0 ${
                done ? 'bg-gradient-to-br from-primary to-yellow-500 text-white' : active ? 'bg-primary/20 text-primary' : 'bg-zinc-800 text-zinc-500'
              }`}>
                {done ? <Check className="w-4 h-4" /> : active ? (
                  <motion.span animate={{ scale: [1, 1.15, 1] }} transition={{ duration: 1, repeat: Infinity }}>
                    <Icon className="w-4 h-4" />
                  </motion.span>
                ) : <Icon className="w-4 h-4" />}
              </span>
              <span className={`text-sm ${done || active ? 'text-white' : 'text-zinc-500'}`}>{s.label}</span>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};

export default ProgramBuildingLoader;
